/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { ccEnum } from '../Utility/CCEnum';
import ComponentReference from '../Utility/Component/ComponentReference/ComponentReference';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import StateComponent from './StateComponent';
import { StateID } from './StateID';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Self Injecting serialized class for [[StateComponent]]s.
 */
@cc._decorator.ccclass("StateReference")
export default class StateReference
{
    public static readonly IsCurrentFilter = (stateReference: StateReference): any =>
    {
        return stateReference.Value && stateReference.Value.IsCurrentState();
    }

    /** ReferenceGroup of [[StateComponent]] to be injected. */
    @property({ type: ccEnum.ReferenceGroup })
    public ReferenceGroup: ReferenceGroup = ReferenceGroup.MainGame;

    /** StateID of [[StateComponent]] to be injected. */
    @property({ type: ccEnum.StateID })
    public StateID: StateID = StateID.Initializing;

    /**
     * Optional custom StateID specifier.
     * ie: State.Custom.[Specifier]
     */
    @property()
    public CustomStateID: string = '';

    /** Direct reference in editor instead of injection. */
    @property({ type: StateComponent })
    public State: StateComponent = null;

    /** Whether the [[StateReference]] being referenced for dependency purposes. */
    @property()
    public Blocking: boolean = false;

    /** @hidden */
    private _value: StateComponent = null;
    /** Accessor to injected [[StateComponent]]. Injects on get. */
    public get Value(): StateComponent { return this.GetValue(); }
    /** Mutator to set [[StateComponent]] explicitly. */
    public set Value(value: StateComponent) { this._value = value; }

    public IsCurrent(): boolean
    {
        return this.Value && this.Value === this.Value.StateManager.CurrentState;
    }

    /** @hidden */
    constructor(
        stateID: StateID = StateID.Initializing,
        isBlocking: boolean = false,
        referenceGroup: ReferenceGroup = null,
        customStateId: string = '')
    {
        this.StateID = stateID;

        this.Blocking = isBlocking;
        if (referenceGroup !== null) { this.ReferenceGroup = referenceGroup; }
        if (customStateId) { this.CustomStateID = customStateId; }
    }

    /**
     * Attempts to inject and return value.
     * @param force True if should re-inject when value exists.
     * @returns Injected [[StateComponent]] or null if not found.
     */
    public GetValue(force: boolean = false): StateComponent
    {
        if (!this._value || force)
        {
            if (this.State)
            {
                this._value = this.State;
            }
            else
            {
                this.State = this.SelfInject();
            }
        }

        return this._value;
    }

    /** Attempts to fill value with injected [[StateComponent]]. */
    public SelfInject = (): StateComponent =>
    {
        this._value = ComponentReference.GetByInstanceID<StateComponent>(
            StateComponent.GetStateID(this.StateID, this.CustomStateID),
            this.ReferenceGroup
        )[0];

        return this._value;
    }
}
